/**
 * Formatting helpers for the Results tab. OWNER: workstream F.
 *
 * House rule for this file: every helper turns a value OpenADS actually wrote
 * to disk into a string. Nothing here derives a "score" the pipeline does not
 * produce, and anything that cannot be computed renders as an em dash rather
 * than as 0 — a clinical read-out must never show a placeholder that looks like
 * a measurement.
 */

import type { LesionLoadTable, QfvTable, SegmentationMetrics } from "@/ipc/contract";

/** Rendered whenever a value is absent. Never substitute 0 for "unknown". */
export const DASH = "—";

// --------------------------------------------------------------- numbers ---

export function formatNumber(value: number | null | undefined, digits = 1): string {
  if (value == null || !Number.isFinite(value)) return DASH;
  return value.toFixed(digits);
}

/** Volumes are reported to 0.1 mL; the pipeline itself prints 2 decimals. */
export function formatMl(value: number | null | undefined, digits = 1): string {
  return formatNumber(value, digits);
}

export function formatPercent(value: number | null | undefined, digits = 1): string {
  if (value == null || !Number.isFinite(value)) return DASH;
  return `${value.toFixed(digits)} %`;
}

export function formatRatio(value: number | null | undefined, digits = 2): string {
  return formatNumber(value, digits);
}

/** Dice/precision/sensitivity from metrics.json; 4 decimals as stored. */
export function formatMetric(value: number | null | undefined, digits = 3): string {
  return formatNumber(value, digits);
}

export function formatCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return DASH;
  return Math.round(value).toLocaleString();
}

/**
 * ASPECTS is a 0..10 score parsed out of the radiological report
 * ("The predicted MCA – ASPECTS is 5."). It is rendered as `n / 10`; the
 * denominator is a constant of the score, not something the pipeline emits.
 */
export const ASPECTS_MAX = 10;

export function formatAspects(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return DASH;
  return String(Math.round(value));
}

/** `mm:ss` / `h:mm:ss` for the Processing Time card. */
export function formatSeconds(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return DASH;
  const total = Math.max(0, Math.round(value));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

// --------------------------------------------------------- volume maths ---

/**
 * The segmentation runs in MNI152 1 mm isotropic space, so one voxel is
 * 1 mm³ = 0.001 mL. `metrics.json` stores `pred_volume` / `true_volume` as raw
 * voxel counts, which is why every mL figure downstream is `vox / 1000`.
 */
export const MNI_VOXEL_VOLUME_MM3 = 1;

/** Voxel volume from a NIfTI `pixdim` (first three entries are the spatial ones). */
export function voxelVolumeMm3(pixdim: number[] | null | undefined): number | null {
  if (!pixdim || pixdim.length < 3) return null;
  const [x, y, z] = pixdim;
  if (![x, y, z].every((v) => Number.isFinite(v) && v > 0)) return null;
  return x * y * z;
}

/** Voxels → mL for an arbitrary voxel volume. 1000 mm³ = 1 mL. */
export function voxelsToMl(voxels: number | null | undefined, voxelVolMm3: number): number | null {
  if (voxels == null || !Number.isFinite(voxels)) return null;
  if (!Number.isFinite(voxelVolMm3) || voxelVolMm3 <= 0) return null;
  return (voxels * voxelVolMm3) / 1000;
}

/** Voxels → mL in MNI152 1 mm space. */
export function mniVoxelsToMl(voxels: number | null | undefined): number | null {
  return voxelsToMl(voxels, MNI_VOXEL_VOLUME_MM3);
}

/**
 * Voxels → mL in the subject's native space. Needs the pixdim of the NIfTI the
 * count came from — native DWI here is 1.72 × 1.72 × 3 mm, so a voxel is ~8.9 mm³
 * and the same lesion has a very different voxel count than in MNI.
 */
export function nativeVoxelsToMl(
  voxels: number | null | undefined,
  pixdim: number[] | null | undefined,
): number | null {
  const vol = voxelVolumeMm3(pixdim);
  return vol == null ? null : voxelsToMl(voxels, vol);
}

/**
 * True when a voxel count and a millilitre figure describe the same object in
 * MNI 1 mm space. Used before printing "N vox" underneath a mL card, so the two
 * numbers on screen can never disagree.
 */
export function voxelCountMatchesMl(
  voxels: number | null | undefined,
  ml: number | null | undefined,
  toleranceMl = 0.05,
): boolean {
  const derived = mniVoxelsToMl(voxels);
  if (derived == null || ml == null) return false;
  return Math.abs(derived - ml) <= toleranceMl;
}

// ------------------------------------------------------- lesion load/QFV ---

/**
 * `*_lesionload.csv` holds raw lesion voxel counts per atlas ROI. The trailing
 * `brain` column is NOT the brain volume: it is the lesion load in the part of
 * the brain outside the atlas' named ROIs (for sub-02e8eb42, ASPECTS ROIs sum to
 * 52,342 voxels and `brain` is 50,460, together ≈ the 103,999-voxel lesion).
 * OpenADS never writes a brain-volume figure, so "% of brain volume" cannot be
 * computed from these files — see the Results tab for what is shown instead.
 */
export const LESION_LOAD_RESIDUAL_KEY = "brain";

export function lesionLoadEntry(table: LesionLoadTable, name: string): number | null {
  const hit = table.entries.find((e) => e.name.toLowerCase() === name.toLowerCase());
  return hit ? hit.voxels : null;
}

/** Residual (`brain`) column, in voxels. See the note above for its meaning. */
export function lesionLoadResidualVoxels(table: LesionLoadTable): number | null {
  return lesionLoadEntry(table, LESION_LOAD_RESIDUAL_KEY);
}

/** Whole lesion load for an atlas = named ROIs + the `brain` residual. */
export function lesionLoadTotalVoxels(table: LesionLoadTable): number {
  return table.entries.reduce((sum, e) => sum + (Number.isFinite(e.voxels) ? e.voxels : 0), 0);
}

/** Share of an atlas' total lesion load that falls in one ROI. */
export function lesionLoadSharePct(voxels: number | null, table: LesionLoadTable): number | null {
  const total = lesionLoadTotalVoxels(table);
  if (voxels == null || total <= 0) return null;
  return (voxels / total) * 100;
}

/**
 * The `volml` column of `*_QFV.csv` is `log10((lesion_voxels + 1) / 1000)`
 * (`reporting_qfv_calculation.py`), i.e. a log10 millilitre value despite the
 * column name. Converting back gives the volume of the mask the QFV features
 * were computed from — which is the `affsyn` mask, so it need not equal the
 * `mni` volume in metrics.json.
 */
export function qfvLogVolumeToMl(logMl: number | null | undefined): number | null {
  if (logMl == null || !Number.isFinite(logMl)) return null;
  return 10 ** logMl;
}

/** QFV values are fractions (portion of the region injured) in 0..1. */
export function formatQfv(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return DASH;
  // Small non-zero QFVs (1.93e-05) must not collapse to "0.000".
  if (value !== 0 && Math.abs(value) < 0.001) return value.toExponential(2);
  return value.toFixed(4);
}

export function qfvTableLabel(table: QfvTable): string {
  return table.atlas;
}

// ------------------------------------------------------------- metrics ----

/**
 * metrics.json only exists when a ground-truth `*_stroke.nii.gz` was supplied;
 * without it the pipeline writes nothing and every field here is null. The
 * Results tab uses this to decide whether the Dice card is shown at all.
 */
export function hasGroundTruth(metrics: SegmentationMetrics | null | undefined): boolean {
  return !!metrics && metrics.dice != null && Number.isFinite(metrics.dice);
}

// ----------------------------------------------------------------- csv ----

/** RFC4180-ish quoting: only quote when needed, double any embedded quotes. */
function csvCell(value: string | number | null | undefined): string {
  if (value == null) return "";
  const text = typeof value === "number" ? String(value) : value;
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function toCsv(header: string[], rows: (string | number | null)[][]): string {
  const lines = [header.map(csvCell).join(",")];
  for (const row of rows) lines.push(row.map(csvCell).join(","));
  return `${lines.join("\n")}\n`;
}

/** File-system-safe name for a client-side CSV download. */
export function csvFileName(subjectId: string, stem: string): string {
  const safe = subjectId.replace(/[^A-Za-z0-9._-]+/g, "_");
  return `${safe}_${stem}.csv`;
}

// --------------------------------------------------------------- paths ----

export function baseName(path: string): string {
  const parts = path.split(/[\\/]/);
  return parts[parts.length - 1] || path;
}

export function dirName(path: string): string {
  const idx = Math.max(path.lastIndexOf("/"), path.lastIndexOf("\\"));
  return idx <= 0 ? path : path.slice(0, idx);
}
